import Auth0 from 'auth0-js';
import CONFIG from 'Config';
import { AUTH_PROVIDERS } from 'Config/constans';
import { getParsedUrlParamsBySearch } from 'Utils/url';
import { isObject } from 'Utils/common';

const isMobile = navigator.userAgent.match(/Android/i)
  || navigator.userAgent.match(/webOS/i)
  || navigator.userAgent.match(/iPhone/i)
  || navigator.userAgent.match(/iPad/i)
  || navigator.userAgent.match(/iPod/i)
  || navigator.userAgent.match(/BlackBerry/i)
  || navigator.userAgent.match(/Windows Phone/i);

const webAuth = new Auth0.WebAuth({
  ...CONFIG.auth0Config,
});

export default class AuthAuth0 {
  constructor() {
    this.webAuth = webAuth;
  }

  get provider() {
    return AUTH_PROVIDERS.AUTH0;
  }

  static _getNormalizedUserData(userData) {
    const normData = {};
    Object.entries(userData).forEach(([key, value]) => {
      if (isObject(value)) {
        normData[key] = AuthAuth0._getNormalizedUserData(userData);
      } else {
        normData[key] = String(value);
      }
    });
    return normData;
  }

  _authorizeReload(config = {}) {
    webAuth.authorize({ ...config });
    return new Promise(() => {});
  }

  _authorizePopup(config = {}) {
    return new Promise((resolve, reject) => {
      webAuth.popup.authorize(config, (err, authResult) => {
        if (err) {
          // Ignoring syntax errors
          if (err.name !== 'SyntaxError') reject(err);
        } else {
          resolve(authResult);
        }
      });
    });
  }

  _login(email, password, realm = CONFIG.auth0DB) {
    return new Promise((resolve, reject) => {
      webAuth.login({
        email,
        password,
        realm,
        popup: true,
      }, (err, authResult) => {
        if (!err) {
          resolve(authResult);
        } else {
          reject(err);
        }
      });
    });
  }

  _getToken(config = {}) {
    return new Promise((resolve, reject) => {
      // Set auth0 response timeout
      const timeoutId = setTimeout(() => {
        reject(new Error('auth_timeout'));
      }, 10000);
      webAuth.checkSession(config, (err, authResult) => {
        // Clear auth0 response timeout
        clearTimeout(timeoutId);
        if (!err) {
          resolve(authResult);
        } else {
          reject(err);
        }
      });
    });
  }

  async _getAuth(config) {
    const result = await this._getToken(config);
    return this._onSuccess(result);
  }

  _getLoginDataByResult(authResult) {
    return {
      token: `Auth0 ${authResult.idToken}`,
      expirationTime: new Date(authResult.idTokenPayload.exp * 1000), // converting sec to ms
      mfa: authResult.idTokenPayload[`${CONFIG.auth0Namespace}use_mfa`],
      email: authResult.idTokenPayload[`${CONFIG.auth0Namespace}email`],
      isFirstLogin: Boolean(authResult.idTokenPayload[`${CONFIG.auth0Namespace}first_login`]),
      isEmailVerified: Boolean(
        authResult.idTokenPayload[`${CONFIG.auth0Namespace}emailVerified`]
        || authResult.idTokenPayload[`${CONFIG.auth0Namespace}email_verified`],
      ),
    };
  }

  _getUserDataByResult(authResult) {
    const { email, name } = authResult.idTokenPayload;
    let { family_name: lastName, given_name: firstName } = authResult.idTokenPayload;
    if (!firstName || !lastName) {
      const parsedName = (name || '').split(' ');
      firstName = firstName || (parsedName.length ? parsedName[0] : '  ');
      lastName = (parsedName.length > 1 ? name.replace(`${firstName} `, '') : firstName);
    }
    const image = authResult.idTokenPayload[`${CONFIG.auth0Namespace}picture`];
    return { email, first_name: firstName, last_name: lastName, image };
  }

  _onSuccess(authResult) {
    return { loginData: this._getLoginDataByResult(authResult) };
  }

  _onSocialLoginSuccess(authResult) {
    const loginData = this._getLoginDataByResult(authResult);
    const userData = this._getUserDataByResult(authResult);
    return { loginData, userData };
  }

  _parseHash() {
    return new Promise((resolve, reject) => {
      webAuth.parseHash((err, authResult) => {
        if (err) {
          reject(err);
        } else {
          resolve(authResult);
        }
      });
    });
  }

  _getRedirectUrlForReload(additionQuery = '') {
    const { origin, search = '', pathname } = window.location;
    const isAuthPage = ['/sign-in', '/sign-up', '/sign-up-social'].includes(pathname);
    const query = search.startsWith('?') ? search.substr(1) : search;
    let queryArray = query.split('&');
    if (additionQuery) {
      // Filter additional query to do not duplicate
      const additionalQueryArray = additionQuery.split('&');
      const additionalQueryFiltered = additionalQueryArray.filter(item => !queryArray.includes(item));
      queryArray = queryArray.concat(additionalQueryFiltered);
    }
    if (isAuthPage) return `${origin}${pathname}?${queryArray.join('&')}`;
    // Return to sign up if it signup process
    const redirectPage = pathname.startsWith('/sign-up') ? 'sign-up' : 'sign-in';
    // If there is redirect query or it's signup process
    if (search.includes('redirect=') || pathname.startsWith('/sign-up')) {
      // return with exist redirect or w/o it
      return `${origin}/${redirectPage}?${queryArray.join('&')}`;
    }
    queryArray.push(`redirect=${pathname}`);
    return `${origin}/${redirectPage}?${queryArray.join('&')}`;
  }

  async init(storageData) {
    // Do not try to auth if it prerender
    if (window.__prerender) throw new Error('No auth in prerender');
    const { hash, search } = window.location;
    // Is need to parse auth result
    const isParseHashNeeded = hash.includes('id_token=');
    if (
      !isParseHashNeeded
      && (!storageData.token || new Date(storageData.expirationTime) < Date.now())
    ) throw new Error('Auth required');
    // Parse search params to object
    const searchParams = getParsedUrlParamsBySearch(search);
    // Is it social login with reload
    const isSocial = searchParams.social;
    // Is it mfa switching
    const isMFA = searchParams.mfa;
    // Is first login
    const isFirstLogin = searchParams.first;
    // If parse needed then parse hash otherwise just get token
    let authResult = isParseHashNeeded ? await this._parseHash() : await this._getToken();
    // If mfa switching call method to complete
    if (isMFA) authResult = await this.switchMFAByURLSearch(searchParams.mfa);
    // Is social complete social login otherwise complete plain login
    const res = isSocial ? this._onSocialLoginSuccess(authResult) : this._onSuccess(authResult);
    Object.assign(res, { isSocial, isMFA, isFirstLogin, isParsedHash: isParseHashNeeded });
    return res;
  }

  async login(email, password, realm) {
    try {
      const authResult = await this._login(email, password, realm);
      return this._onSuccess(authResult);
    } catch (error) {
      if (error.description === 'Multifactor authentication required') {
        const redirectUri = this._getRedirectUrlForReload();
        await this._authorizeReload({ redirectUri });
      }
      throw error;
    }
  }

  async socialLoginPopup(options) {
    const authResult = await this._authorizePopup({ ...options });
    return this._onSocialLoginSuccess(authResult);
  }

  async socialLoginReload(options) {
    const redirectUri = this._getRedirectUrlForReload('social=true');
    await this._authorizeReload({ ...options, redirectUri });
  }

  async socialLogin(connection, options = {}) {
    const requestOptions = { ...options, connection };
    return isMobile
      ? this.socialLoginReload(requestOptions)
      : this.socialLoginPopup(requestOptions);
  }

  register(email, password, userData = {}) {
    return new Promise((resolve, reject) => {
      webAuth.signup({
        connection: CONFIG.auth0DB,
        email,
        password,
        user_metadata: AuthAuth0._getNormalizedUserData(userData),
      }, (err) => {
        if (err) {
          reject(err);
          return;
        }
        this.login(email, password)
          .then(resolve)
          .catch(reject);
      });
    });
  }

  logout() {
    return new Promise(((resolve) => {
      const iframe = document.createElement('iframe');
      iframe.src = webAuth.client.buildLogoutUrl({
        returnTo: CONFIG.auth0Config.redirectUri,
        clientID: CONFIG.auth0Config.clientID,
      });
      iframe.style.display = 'none';
      document.body.appendChild(iframe);
      iframe.addEventListener('load', () => {
        setTimeout(() => document.body.removeChild(iframe), 2000);
        resolve();
      });
    }));
  }

  resetPassword(email) {
    return new Promise((resolve, reject) => {
      webAuth.changePassword({
        connection: CONFIG.auth0DB,
        email,
      }, (err, resp) => {
        if (err) {
          reject(err);
        } else {
          resolve(resp);
        }
      });
    });
  }

  refreshToken() {
    return this._getAuth();
  }

  async switchMFAByURLSearch(status) {
    if (status === 'enable') {
      return this.enableMFA(true);
    }
    return this.disableMFA(true);
  }

  async enableMFA(isMFAPassed, { forceReload, isFirstLogin } = {}) {
    if (!isMFAPassed) {
      if (isMobile || forceReload) {
        let additionalQuery = 'mfa=enable';
        // Save ifs first login for enabling mfa during signup
        if (isFirstLogin) additionalQuery += '&first=true';
        const redirectUri = this._getRedirectUrlForReload(additionalQuery);
        await this._authorizeReload({ scope: 'read:authenticators', redirectUri });
      } else {
        await this._authorizePopup({ scope: 'read:authenticators' });
      }
    }
    return this._getToken({ scope: 'write:mfa' });
  }

  async disableMFA(isMFAPassed) {
    if (!isMFAPassed) {
      if (isMobile) {
        const redirectUri = this._getRedirectUrlForReload('mfa=disable');
        await this._authorizeReload({ scope: 'remove:authenticators', redirectUri });
      } else {
        await this._authorizePopup({ scope: 'remove:authenticators' });
      }
    }
    return this._getToken({ scope: 'remove:mfa' });
  }

  async sendEmailVerification() {
    return this._getToken({ scope: 'request:email-verification' });
  }
}
